/**
 * Types + runtime config + WebSocket hook for the /calls observability page.
 *
 * The SPA fetches /runtime-config.json (written by CDK on deploy) for the
 * read API and WebSocket URLs. Falls back to dev defaults if missing.
 */

export type Path = "A" | "B" | "C";

export interface CallEventBase {
  callId: string;
  ts: string;          // ISO 8601
  path: Path | string;
  type: string;
  data?: Record<string, unknown>;
}

export interface CallMeta {
  callId: string;
  path: Path | string;
  startedAt: string;
  endedAt?: string;
  durationMs?: number;
  status: "active" | "ended";
  outcome?: string;
  fromNumber?: string;
  channel?: string;
}

export interface CallTurn {
  callId: string;
  type: string;        // user_turn, agent_turn, tool_call, tool_result, intent, slot
  ts: string;
  data: Record<string, unknown>;
}

export interface RuntimeConfig {
  eventsReadApiUrl: string;
  eventsWsUrl: string;
}

let _configPromise: Promise<RuntimeConfig> | null = null;

export function getRuntimeConfig(): Promise<RuntimeConfig> {
  if (_configPromise) return _configPromise;
  _configPromise = fetch("/runtime-config.json", { cache: "no-store" })
    .then((r) => (r.ok ? r.json() : {}))
    .then((j: Partial<RuntimeConfig>) => ({
      eventsReadApiUrl: (j.eventsReadApiUrl || "").replace(/\/$/, ""),
      eventsWsUrl: j.eventsWsUrl || "",
    }))
    .catch(() => ({ eventsReadApiUrl: "", eventsWsUrl: "" }));
  return _configPromise;
}

export async function fetchRecentCalls(limit = 50): Promise<CallMeta[]> {
  const cfg = await getRuntimeConfig();
  if (!cfg.eventsReadApiUrl) return [];
  const r = await fetch(`${cfg.eventsReadApiUrl}/calls?limit=${limit}`, { cache: "no-store" });
  if (!r.ok) return [];
  const j = await r.json();
  return (j.calls || []) as CallMeta[];
}

export async function fetchCall(callId: string): Promise<{ call: CallMeta | null; turns: CallTurn[] }> {
  const cfg = await getRuntimeConfig();
  if (!cfg.eventsReadApiUrl) return { call: null, turns: [] };
  const r = await fetch(`${cfg.eventsReadApiUrl}/calls/${encodeURIComponent(callId)}`, { cache: "no-store" });
  if (!r.ok) return { call: null, turns: [] };
  const j = await r.json();
  return { call: j.call ?? null, turns: (j.turns || []) as CallTurn[] };
}

export async function deleteCall(callId: string): Promise<boolean> {
  const cfg = await getRuntimeConfig();
  if (!cfg.eventsReadApiUrl) return false;
  const r = await fetch(`${cfg.eventsReadApiUrl}/calls/${encodeURIComponent(callId)}`, {
    method: "DELETE",
  });
  return r.ok;
}

export async function deleteAllCalls(): Promise<number> {
  const cfg = await getRuntimeConfig();
  if (!cfg.eventsReadApiUrl) return 0;
  const r = await fetch(`${cfg.eventsReadApiUrl}/calls`, { method: "DELETE" });
  if (!r.ok) return 0;
  const j = await r.json();
  return Number(j.items || 0);
}

/** Opens a single WebSocket, dispatches every event to the listener.
 *  Auto-reconnect with backoff. Returns a close handle. */
export function openEventsWebSocket(onEvent: (e: CallEventBase) => void): { close: () => void } {
  let closed = false;
  let socket: WebSocket | null = null;
  let backoffMs = 500;

  const connect = async () => {
    if (closed) return;
    const cfg = await getRuntimeConfig();
    if (!cfg.eventsWsUrl) {
      // No URL yet (pre-deploy). Try again later.
      setTimeout(connect, 5_000);
      return;
    }
    try {
      socket = new WebSocket(cfg.eventsWsUrl);
      socket.addEventListener("open", () => {
        backoffMs = 500;
      });
      socket.addEventListener("message", (ev) => {
        try {
          const e = JSON.parse(String(ev.data)) as CallEventBase;
          if (e && e.callId && e.type) onEvent(e);
        } catch {
          // ignore malformed
        }
      });
      socket.addEventListener("close", () => {
        if (closed) return;
        setTimeout(connect, backoffMs);
        backoffMs = Math.min(backoffMs * 2, 8_000);
      });
      socket.addEventListener("error", () => {
        try {
          socket?.close();
        } catch {
          /* ignore */
        }
      });
    } catch {
      if (!closed) setTimeout(connect, backoffMs);
    }
  };

  void connect();
  return {
    close: () => {
      closed = true;
      try {
        socket?.close();
      } catch {
        /* ignore */
      }
    },
  };
}
